import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import "./LiveTrainingDuckMode.css";

/**
 * LiveTrainingDuckMode:
 * Renders a 100% pitch-black OLED energy-saver shield.
 * Powers off all display pixels to save battery and stop phone overheating
 * while the back camera films table rallies.
 * Screen wake lock remains active so the OS does not sleep the camera stream.
 * Tap anywhere immediately exits Duck Mode.
 */
export function LiveTrainingDuckMode({
  active = false,
  onWake,
  tableNumber = null,
  isRecording = false,
  timerDisplay = null,
  exerciseName = "",
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!active) return undefined;

    const handleKeyDown = (e) => {
      // Any key or escape wakes the screen
      onWake?.();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [active, onWake]);

  if (!active) return null;

  return (
    <div
      className="lt-duck-mode"
      onClick={() => onWake?.()}
      role="region"
      aria-label={t("liveTrainingDuckModeShield", "Duck Mode screen shield")}
      data-testid="lt-duck-mode-shield"
      title={t("liveTrainingDuckModeTapToWake", "Tap anywhere to wake screen")}
    >
      <div className="lt-duck-mode__dim-hud">
        <div className="lt-duck-mode__status-row">
          <span
            className={`lt-duck-mode__dot${
              isRecording ? " lt-duck-mode__dot--rec" : ""
            }`}
          >
            <FiberManualRecordIcon fontSize="inherit" />
          </span>
          <span className="lt-duck-mode__status-text">
            {isRecording
              ? t("liveTrainingDuckRecording", "BACK CAM · RECORDING ACTIVE")
              : t("liveTrainingDuckStandby", "BACK CAM · STANDBY")}
          </span>
          {tableNumber != null ? (
            <span className="lt-duck-mode__table-badge">T#{tableNumber}</span>
          ) : null}
        </div>

        {timerDisplay ? (
          <div className="lt-duck-mode__timer">{timerDisplay}</div>
        ) : null}

        {exerciseName ? (
          <div className="lt-duck-mode__ex-name">{exerciseName}</div>
        ) : null}

        <div className="lt-duck-mode__wake-hint">
          <WbSunnyIcon fontSize="small" className="lt-duck-mode__wake-icon" />
          <span>
            {t(
              "liveTrainingDuckModeTapToWake",
              "OLED Energy Saver · Tap anywhere to wake"
            )}
          </span>
        </div>

        <button
          type="button"
          className="lt-duck-mode__wake-btn"
          onClick={(e) => {
            e.stopPropagation();
            onWake?.();
          }}
        >
          <PowerSettingsNewIcon fontSize="small" />
          <span>{t("liveTrainingDuckModeWake", "Wake Screen")}</span>
        </button>
      </div>
    </div>
  );
}
